import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  MapPin, ChevronRight, Users, Clock, Plus, AlertCircle, Shield,
} from "lucide-react";
import { toast } from "sonner";

function PermissionGate({ children }) {
  const { data: perms, isLoading } = useQuery({
    queryKey: ["member-permissions"],
    queryFn: () => api.get("/member/permissions"),
  });
  const [enabling, setEnabling] = useState(false);

  const grantMut = useMutation({
    mutationFn: () =>
      api.post("/member/permissions", {
        permission_type: "CONNECTED_ACTIVITY",
        granted: true,
        consent_text: "I agree to share my preferred name and member code with other members in Find a Game.",
        language: "en",
      }),
    onSuccess: () => {
      toast.success("Connected Activity enabled");
      window.location.reload();
    },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  const connected = perms?.permissions?.CONNECTED_ACTIVITY;
  if (connected?.granted) return children;

  return (
    <div className="max-w-lg mx-auto py-16 px-4 text-center" data-testid="games-permission-gate">
      <Shield size={32} className="text-bronze mx-auto mb-5" />
      <h2 className="font-serif text-2xl text-cream tracking-wide mb-3">
        Find a Game
      </h2>
      <p className="text-sm text-sand/70 leading-relaxed mb-6 max-w-sm mx-auto">
        To browse and join games with other members, please enable Connected Activity.
        Your preferred name and LV member code will be visible to other participants.
      </p>
      {!enabling ? (
        <button
          onClick={() => setEnabling(true)}
          className="lv-btn-primary min-h-[48px] px-8"
          data-testid="games-enable-permission-btn"
        >
          Enable Connected Activity
        </button>
      ) : (
        <div
          className="bg-walnut border border-lv-border p-6 text-left mx-auto max-w-sm"
          data-testid="games-permission-confirm"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-lv-muted mb-3">
            Permission Request
          </p>
          <p className="text-sm text-sand/80 leading-relaxed mb-5">
            I agree to share my preferred name and LV member code with other members
            when participating in Find a Game.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => grantMut.mutate()}
              disabled={grantMut.isPending}
              className="lv-btn-primary flex-1 min-h-[44px]"
              data-testid="games-confirm-permission-btn"
            >
              {grantMut.isPending ? "Enabling\u2026" : "Agree & Enable"}
            </button>
            <button
              onClick={() => setEnabling(false)}
              className="lv-btn-secondary flex-1 min-h-[44px]"
              data-testid="games-cancel-permission-btn"
            >
              Not Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GameCard({ game }) {
  const d = game.game_datetime ? new Date(game.game_datetime) : null;
  return (
    <Link
      to={`/games/${game.id}`}
      className="block group"
      data-testid={`game-card-${game.id}`}
    >
      <div className="bg-walnut border border-lv-border p-5 transition-all duration-300 hover:border-bronze/40">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {d && (
              <p className="text-[10px] tracking-[0.3em] uppercase text-bronze mb-1.5">
                {d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                {" \u00b7 "}
                {d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </p>
            )}
            <h3 className="font-serif text-lg text-cream tracking-wide group-hover:text-sand transition-colors">
              {game.title}
            </h3>
            {game.description && (
              <p className="text-sm text-sand/60 mt-1.5 leading-relaxed line-clamp-2">
                {game.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
              {game.location && (
                <div className="flex items-center gap-1.5">
                  <MapPin size={11} className="text-lv-muted/60 shrink-0" />
                  <span className="text-[11px] text-lv-muted/70 tracking-wide">{game.location}</span>
                </div>
              )}
              {game.level_preference && (
                <span className="text-[10px] tracking-[0.2em] uppercase text-lv-muted/70">
                  {game.level_preference}
                </span>
              )}
              <div className="flex items-center gap-1.5">
                <Users size={11} className="text-lv-muted/50 shrink-0" />
                <span className="text-[11px] text-lv-muted/60">
                  {game.interest_count} interested
                </span>
              </div>
            </div>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1">
            {game.is_mine && (
              <span className="text-[9px] tracking-[0.2em] uppercase text-bronze/70 bg-bronze/10 px-2 py-0.5 rounded-sm">
                Yours
              </span>
            )}
            <ChevronRight size={16} className="text-lv-muted/40 group-hover:text-bronze transition-colors" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function GamesList() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["member-games"],
    queryFn: () => api.get("/games"),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="games-loading">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32 px-6" data-testid="games-error">
        <p className="text-cream text-sm">{error.message}</p>
      </div>
    );
  }

  const games = data || [];

  return (
    <div className="max-w-3xl mx-auto" data-testid="games-page">
      <div className="flex items-start justify-between gap-4 mb-10 sm:mb-14">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl text-cream tracking-wide">
            Find a Game
          </h1>
          <p className="text-sm text-lv-muted tracking-wide mt-2">
            Open games in your chapter
          </p>
        </div>
        <button
          onClick={() => navigate("/games/new")}
          className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px] px-5 shrink-0"
          data-testid="games-create-btn"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Post a Game</span>
          <span className="sm:hidden">New</span>
        </button>
      </div>

      {games.length === 0 ? (
        <div className="text-center py-16" data-testid="games-empty">
          <Users size={28} className="text-lv-muted/30 mx-auto mb-4" />
          <p className="text-sm text-lv-muted tracking-wide mb-6">
            No open games right now
          </p>
          <button
            onClick={() => navigate("/games/new")}
            className="lv-btn-secondary min-h-[44px] px-6"
            data-testid="games-empty-create-btn"
          >
            Be the first to post
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="games-list">
          {games.map((g) => (
            <GameCard key={g.id} game={g} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Games() {
  return (
    <PermissionGate>
      <GamesList />
    </PermissionGate>
  );
}
